// Armado de un presupuesto. En BORRADOR se edita; una vez enviado se muestra
// en modo lectura, porque los precios que ya vio el cliente no se tocan.

import { api } from "../api";
import { esc } from "../html";
import { imprimirHtml, documentoImpreso } from "../imprimir";
import { iconoVolver, iconoMas, iconoCerrar } from "../iconos";
import {
  ETIQUETA_ESTADO_PRESUPUESTO,
  ETIQUETA_CATEGORIA,
  claseEstadoPresupuesto,
} from "../etiquetas";
import { MONEDAS } from "../../lib/tipos";
import type {
  Condicion,
  CondicionAdjunta,
  FichaPresupuesto,
  Presupuesto,
  PresupuestoItem,
  Moneda,
} from "../../lib/tipos";
import {
  subtotal,
  totalPresupuesto,
  formatearMonto,
  formatearCantidad,
  parsearMonto,
} from "../../lib/calculo";
import { formatoLocal } from "../../lib/fecha";

interface Renglon {
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
}

function filaEditable(r: Renglon, i: number, moneda: Moneda): string {
  return `
    <div class="renglon rounded-xl border border-borde bg-superficie p-3" data-i="${i}">
      <div class="flex items-start gap-2">
        <input class="campo rDesc flex-1" placeholder="Qué se hace" maxlength="300"
               value="${esc(r.descripcion)}" />
        <button type="button" class="rBorrar chip-borrar mt-1 shrink-0 text-apagado hover:text-alerta"
                aria-label="Sacar renglón">${iconoCerrar}</button>
      </div>
      <div class="mt-2 grid grid-cols-3 items-end gap-2">
        <label><span class="etiqueta">Cantidad</span>
          <input class="campo rCant" type="number" step="0.25" min="0.25" value="${r.cantidad}" /></label>
        <label><span class="etiqueta">Precio</span>
          <input class="campo rPrecio" type="text" inputmode="decimal"
                 value="${formatearMonto(r.precio_unitario, moneda)}" /></label>
        <div class="pb-2 text-right">
          <span class="etiqueta">Subtotal</span>
          <span class="rSub font-semibold">${formatearMonto(
            subtotal(r.cantidad, r.precio_unitario),
            moneda,
          )}</span>
        </div>
      </div>
    </div>`;
}

function filaLectura(r: PresupuestoItem, moneda: Moneda): string {
  return `
    <div class="fila flex items-start justify-between gap-3 p-3">
      <div class="min-w-0 flex-1">
        <div class="break-words">${esc(r.descripcion)}</div>
        <div class="mt-0.5 text-xs text-apagado">
          ${formatearCantidad(r.cantidad)} × ${formatearMonto(r.precio_unitario, moneda)}
        </div>
      </div>
      <span class="shrink-0 font-semibold">${formatearMonto(
        subtotal(r.cantidad, r.precio_unitario),
        moneda,
      )}</span>
    </div>`;
}

/** El texto que sale impreso: el retoque del presupuesto, o el del catálogo. */
const textoCondicion = (c: CondicionAdjunta) => c.texto_override ?? c.texto;

/** Cláusulas del presupuesto: elegibles en borrador, de lectura después. */
function seccionCondiciones(
  adjuntas: CondicionAdjunta[],
  catalogo: Condicion[],
  editable: boolean,
): string {
  if (!editable) {
    if (!adjuntas.length) return "";
    return `
      <section class="mt-6">
        <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">
          Condiciones de trabajo
        </h2>
        <div class="space-y-2">
          ${adjuntas
            .map(
              (c) => `
            <div class="fila p-3">
              <div class="flex items-center gap-2">
                <span class="chip">${ETIQUETA_CATEGORIA[c.categoria]}</span>
                <span class="font-medium">${esc(c.titulo)}</span>
              </div>
              <p class="mt-1.5 whitespace-pre-wrap text-sm text-apagado">${esc(textoCondicion(c))}</p>
            </div>`,
            )
            .join("")}
        </div>
      </section>`;
  }

  if (!catalogo.length) {
    return `<p class="mt-6 text-sm text-apagado">
      No hay cláusulas en el catálogo. <a href="#/condiciones" class="text-acento hover:underline">Cargalas acá</a>.
    </p>`;
  }

  const adjunta = (id: number) => adjuntas.find((a) => a.condicion_id === id);

  return `
    <section class="mt-6">
      <h2 class="mb-2 text-sm font-semibold uppercase tracking-wider text-apagado">
        Condiciones de trabajo
      </h2>
      <div class="space-y-2">
        ${catalogo
          .map((c) => {
            const a = adjunta(c.id);
            return `
          <div class="condicion fila p-3" data-id="${c.id}">
            <label class="flex items-start gap-3">
              <input type="checkbox" class="cChk mt-1 h-5 w-5 shrink-0 accent-acento"${a ? " checked" : ""} />
              <span class="min-w-0 flex-1">
                <span class="flex flex-wrap items-center gap-2">
                  <span class="chip">${ETIQUETA_CATEGORIA[c.categoria]}</span>
                  <span class="font-medium">${esc(c.titulo)}</span>
                  ${c.tareas ? `<span class="chip chip-acento">dispara ${c.tareas}</span>` : ""}
                </span>
                <span class="mt-1 block whitespace-pre-wrap text-sm text-apagado">${esc(c.texto)}</span>
              </span>
            </label>
            <details class="mt-2 ${a?.texto_override ? "" : "opacity-70"}">
              <summary class="text-xs text-apagado">Retocar el texto sólo para este presupuesto</summary>
              <textarea class="cTexto campo mt-2" rows="3" maxlength="4000"
                        placeholder="Dejalo vacío para usar el del catálogo">${esc(
                          a?.texto_override,
                        )}</textarea>
            </details>
          </div>`;
          })
          .join("")}
      </div>
    </section>`;
}

/**
 * Nombre del PDF. Va el proyecto porque es como Fernando los busca después;
 * si el presupuesto todavía no tiene proyecto, queda el cliente.
 */
function tituloDeLaHoja(p: Presupuesto): string {
  const de = p.proyecto_nombre || p.cliente_nombre;
  return de ? `Presupuesto ${p.numero} - ${de}` : `Presupuesto ${p.numero}`;
}

/** El papel. HTML aparte, con estilos propios: no hereda nada de la app. */
function hojaImpresa(
  p: Presupuesto,
  items: PresupuestoItem[],
  condiciones: CondicionAdjunta[],
): string {
  const filas = items
    .map(
      (i) => `<tr>
        <td>${esc(i.descripcion)}</td>
        <td class="num">${formatearCantidad(i.cantidad)}</td>
        <td class="num">${formatearMonto(i.precio_unitario, p.moneda)}</td>
        <td class="num">${formatearMonto(subtotal(i.cantidad, i.precio_unitario), p.moneda)}</td>
      </tr>`,
    )
    .join("");

  // El esqueleto y el CSS viven en documentoImpreso(): son los mismos del
  // contrato. Acá sólo se arma el cuerpo.
  const cuerpo = `
  <h1>Presupuesto N.º ${p.numero}</h1>
  <div class="gris">${formatoLocal(p.fecha)}</div>
  <div class="datos">
    <div><b>Cliente:</b> ${esc(p.cliente_nombre)}</div>
    ${p.proyecto_nombre ? `<div><b>Proyecto:</b> ${esc(p.proyecto_nombre)}</div>` : ""}
    <div><b>Moneda:</b> ${p.moneda}</div>
  </div>
  <table>
    <thead><tr>
      <th>Descripción</th><th class="num">Cant.</th><th class="num">Precio</th><th class="num">Subtotal</th>
    </tr></thead>
    <tbody>${filas}</tbody>
  </table>
  <div class="total">Total: ${formatearMonto(p.total, p.moneda)}</div>
  ${p.notas ? `<div class="notas">${esc(p.notas)}</div>` : ""}
  ${
    condiciones.length
      ? `<section class="condiciones">
           <h2>Condiciones de trabajo</h2>
           ${condiciones
             .map(
               (c) => `<div class="clausula">
                  <b>${esc(c.titulo)}</b>
                  <div>${esc(textoCondicion(c))}</div>
                </div>`,
             )
             .join("")}
         </section>`
      : ""
  }
  <footer>Válido por ${p.validez_dias} días, hasta el ${formatoLocal(p.vence_el)}.</footer>`;

  return documentoImpreso(esc(tituloDeLaHoja(p)), cuerpo);
}

export async function renderPresupuesto(
  contenedor: HTMLElement,
  params: Record<string, string>,
): Promise<void> {
  const id = Number(params.id);
  if (!Number.isInteger(id) || id <= 0) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">Ese presupuesto no existe.</div>`;
    return;
  }

  /** Lee los renglones del DOM. Es la fuente de verdad mientras se edita. */
  function leerRenglones(): Renglon[] {
    return [...contenedor.querySelectorAll<HTMLElement>(".renglon")].map((el) => ({
      descripcion: (el.querySelector(".rDesc") as HTMLInputElement).value.trim(),
      cantidad: Number((el.querySelector(".rCant") as HTMLInputElement).value) || 0,
      precio_unitario:
        parsearMonto((el.querySelector(".rPrecio") as HTMLInputElement).value) ?? 0,
    }));
  }

  /** Cláusulas tildadas, con su retoque si lo tiene. */
  function leerCondiciones(): { condicion_id: number; texto_override: string | null }[] {
    return [...contenedor.querySelectorAll<HTMLElement>(".condicion")]
      .filter((el) => (el.querySelector(".cChk") as HTMLInputElement).checked)
      .map((el) => ({
        condicion_id: Number(el.dataset.id),
        texto_override:
          (el.querySelector(".cTexto") as HTMLTextAreaElement).value.trim() || null,
      }));
  }

  async function pintar(): Promise<void> {
    const f = await api.get<FichaPresupuesto>(`/api/presupuestos/${id}`);
    const p = f.presupuesto;
    const editable = p.estado === "BORRADOR";
    const catalogo = editable ? await api.get<Condicion[]>("/api/condiciones") : [];
    let renglones: Renglon[] = f.items.map((i) => ({
      descripcion: i.descripcion,
      cantidad: i.cantidad,
      precio_unitario: i.precio_unitario,
    }));

    const acciones: Record<string, string> = {};
    if (p.estado === "BORRADOR") acciones["ENVIADO"] = "Marcar enviado";
    if (p.estado === "ENVIADO") {
      acciones["ACEPTADO"] = "Aceptado";
      acciones["RECHAZADO"] = "Rechazado";
    }
    // La salida para el typo: vuelve a ser editable y queda en el registro.
    if (p.estado === "ENVIADO" || p.estado === "RECHAZADO") {
      acciones["BORRADOR"] = "Volver a borrador";
    }
    // Se puede intentar borrar siempre; el backend corta si generó trabajo
    // que sigue vivo. Esconder el botón sólo hacía que uno no entienda por qué.
    const borrable = true;

    contenedor.innerHTML = `
      <a href="#/presupuestos" class="mb-3 inline-flex items-center gap-1 text-sm text-apagado hover:text-texto">
        ${iconoVolver} Presupuestos
      </a>

      <div class="flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <h1 class="titulo-pagina">Presupuesto N.º ${p.numero}</h1>
          <p class="subtitulo mt-0.5">${esc(p.cliente_nombre)}${
            p.proyecto_nombre ? " · " + esc(p.proyecto_nombre) : ""
          }</p>
        </div>
        <span class="${claseEstadoPresupuesto(p.estado_efectivo)}">${
          ETIQUETA_ESTADO_PRESUPUESTO[p.estado_efectivo]
        }</span>
      </div>

      <p class="mt-1 text-xs text-apagado">
        ${formatoLocal(p.fecha)} · vale ${p.validez_dias} días, hasta el ${formatoLocal(p.vence_el)}
      </p>

      ${
        editable
          ? `<div class="mt-4 grid grid-cols-2 gap-3">
               <label><span class="etiqueta">Moneda</span>
                 <select id="moneda" class="campo">
                   ${MONEDAS.map(
                     (m) => `<option value="${m}"${m === p.moneda ? " selected" : ""}>${m}</option>`,
                   ).join("")}
                 </select></label>
               <label><span class="etiqueta">Validez (días)</span>
                 <input id="validez" type="number" class="campo" value="${p.validez_dias}" min="1" max="365" /></label>
             </div>`
          : `<div class="tarjeta mt-4 text-sm text-apagado">
               Este presupuesto ya salió, así que no se edita. Si cambió algo, anulalo y hacé uno nuevo.
             </div>`
      }

      <div id="renglones" class="mt-4 space-y-2">
        ${
          editable
            ? renglones.map((r, i) => filaEditable(r, i, p.moneda)).join("")
            : f.items.map((i) => filaLectura(i, p.moneda)).join("") ||
              `<div class="tarjeta text-center text-apagado">Sin renglones.</div>`
        }
      </div>

      ${
        editable
          ? `<button id="bAgregar" class="btn-sec mt-2 w-full">${iconoMas} Agregar renglón</button>`
          : ""
      }

      <div class="mt-4 flex items-center justify-between border-t border-borde pt-3">
        <span class="text-sm uppercase tracking-wider text-apagado">Total</span>
        <span id="total" class="text-2xl font-bold">${formatearMonto(p.total, p.moneda)}</span>
      </div>

      ${
        editable
          ? `<label class="mt-4 block"><span class="etiqueta">Notas</span>
               <textarea id="notas" class="campo" rows="3" maxlength="4000">${esc(p.notas)}</textarea></label>`
          : p.notas
            ? `<p class="mt-4 whitespace-pre-wrap text-sm text-apagado">${esc(p.notas)}</p>`
            : ""
      }

      ${seccionCondiciones(f.condiciones, catalogo, editable)}

      <p id="err" class="mt-3 text-sm text-alerta"></p>

      <div class="mt-3 flex flex-wrap gap-2">
        ${editable ? `<button id="bGuardar" class="btn-primario flex-1">Guardar</button>` : ""}
        <button id="bImprimir" class="btn-sec flex-1">Imprimir / PDF</button>
        ${
          p.estado === "ACEPTADO"
            ? `<button id="bContrato" class="btn-primario flex-1">Generar contrato</button>`
            : ""
        }
      </div>

      <div class="mt-2 flex flex-wrap gap-2">
        ${Object.entries(acciones)
          .map(
            ([estado, texto]) =>
              `<button class="bEstado btn-sec flex-1" data-estado="${estado}">${texto}</button>`,
          )
          .join("")}
        <button id="bDuplicar" class="btn-sec">Duplicar</button>
        ${
          p.estado !== "ANULADO"
            ? `<button id="bAnular" class="btn-sec text-apagado">Anular</button>`
            : ""
        }
        ${
          borrable
            ? `<button id="bBorrar" class="btn-sec text-alerta">Borrar</button>`
            : ""
        }
      </div>

      ${
        p.estado !== "BORRADOR"
          ? `<p class="mt-3 text-xs text-apagado">
               Para cambiarle algo a un presupuesto que ya salió tenés dos caminos:
               <b>Duplicar</b> —queda la versión vieja y trabajás sobre la nueva— o
               ${
                 acciones["BORRADOR"]
                   ? "<b>Volver a borrador</b>, si en realidad nunca lo mandaste."
                   : "anularlo y hacer uno nuevo."
               }
             </p>`
          : ""
      }`;

    const err = contenedor.querySelector("#err") as HTMLElement;
    const elTotal = contenedor.querySelector("#total") as HTMLElement;
    const monedaActual = () =>
      ((contenedor.querySelector("#moneda") as HTMLSelectElement | null)?.value ??
        p.moneda) as Moneda;

    /** Recalcula subtotales y total con el mismo código que usa el servidor. */
    function recalcular(): void {
      const moneda = monedaActual();
      const rs = leerRenglones();
      contenedor.querySelectorAll<HTMLElement>(".renglon").forEach((el, i) => {
        const s = el.querySelector(".rSub") as HTMLElement;
        s.textContent = formatearMonto(subtotal(rs[i].cantidad, rs[i].precio_unitario), moneda);
      });
      elTotal.textContent = formatearMonto(totalPresupuesto(rs), moneda);
    }

    function engancharRenglones(): void {
      contenedor.querySelectorAll<HTMLInputElement>(".rCant, .rPrecio, .rDesc").forEach((i) => {
        i.addEventListener("input", recalcular);
      });
      // Al salir del campo, el precio se muestra prolijo: '2500' -> '$ 2.500,00'
      contenedor.querySelectorAll<HTMLInputElement>(".rPrecio").forEach((i) => {
        i.addEventListener("blur", () => {
          const c = parsearMonto(i.value);
          if (c !== null) i.value = formatearMonto(c, monedaActual());
          recalcular();
        });
      });
      contenedor.querySelectorAll<HTMLButtonElement>(".rBorrar").forEach((b) => {
        b.addEventListener("click", () => {
          b.closest(".renglon")?.remove();
          recalcular();
        });
      });
    }

    if (editable) {
      engancharRenglones();

      contenedor.querySelector("#moneda")?.addEventListener("change", recalcular);

      contenedor.querySelector("#bAgregar")?.addEventListener("click", () => {
        const cont = contenedor.querySelector("#renglones") as HTMLElement;
        const i = cont.querySelectorAll(".renglon").length;
        cont.insertAdjacentHTML(
          "beforeend",
          filaEditable({ descripcion: "", cantidad: 1, precio_unitario: 0 }, i, monedaActual()),
        );
        engancharRenglones();
        (cont.lastElementChild?.querySelector(".rDesc") as HTMLInputElement)?.focus();
      });

      contenedor.querySelector("#bGuardar")?.addEventListener("click", async () => {
        err.textContent = "";
        const rs = leerRenglones();
        if (rs.some((r) => !r.descripcion)) {
          err.textContent = "Hay un renglón sin descripción";
          return;
        }
        if (rs.some((r) => r.cantidad <= 0)) {
          err.textContent = "Hay un renglón con cantidad cero";
          return;
        }
        try {
          await api.put(`/api/presupuestos/${id}`, {
            moneda: monedaActual(),
            validez_dias: Number((contenedor.querySelector("#validez") as HTMLInputElement).value),
            notas: (contenedor.querySelector("#notas") as HTMLTextAreaElement).value.trim() || null,
            items: rs,
          });
          await api.put(`/api/presupuestos/${id}/condiciones`, {
            condiciones: leerCondiciones(),
          });
          await pintar();
        } catch (e) {
          err.textContent = e instanceof Error ? e.message : "No se pudo guardar";
        }
      });
    }

    contenedor.querySelector("#bImprimir")?.addEventListener("click", async () => {
      // Se imprime lo que está guardado, no lo que hay en pantalla sin guardar.
      const actual = await api.get<FichaPresupuesto>(`/api/presupuestos/${id}`);
      imprimirHtml(
        hojaImpresa(actual.presupuesto, actual.items, actual.condiciones),
        tituloDeLaHoja(actual.presupuesto),
      );
    });

    // El contrato nace acá: foto del presupuesto aceptado, y a la ficha nueva.
    contenedor.querySelector("#bContrato")?.addEventListener("click", async () => {
      err.textContent = "";
      try {
        const c = await api.post<{ id: number }>("/api/contratos", { presupuesto_id: id });
        location.hash = `#/contrato/${c.id}`;
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo generar el contrato";
      }
    });

    contenedor.querySelectorAll<HTMLButtonElement>(".bEstado").forEach((b) => {
      b.addEventListener("click", async () => {
        err.textContent = "";
        try {
          const r = await api.post<{ tareas_generadas: number }>(
            `/api/presupuestos/${id}/estado`,
            { estado: b.dataset.estado },
          );
          if (r.tareas_generadas) {
            alert(
              `Se crearon ${r.tareas_generadas} tareas de las condiciones firmadas. Están en el proyecto.`,
            );
          }
          await pintar();
        } catch (e) {
          err.textContent = e instanceof Error ? e.message : "No se pudo cambiar el estado";
        }
      });
    });

    contenedor.querySelector("#bAnular")?.addEventListener("click", async () => {
      if (!confirm("¿Anular el presupuesto? Queda guardado, pero no se puede reactivar.")) return;
      await api.del(`/api/presupuestos/${id}`);
      await pintar();
    });

    contenedor.querySelector("#bDuplicar")?.addEventListener("click", async () => {
      err.textContent = "";
      try {
        const copia = await api.post<{ id: number; numero: number }>(
          `/api/presupuestos/${id}/duplicar`,
        );
        location.hash = `#/presupuesto/${copia.id}`;
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo duplicar";
      }
    });

    contenedor.querySelector("#bBorrar")?.addEventListener("click", async () => {
      if (
        !confirm(
          `¿Borrar el presupuesto Nº ${p.numero} para siempre?\n\n` +
            "Esto NO es anular: desaparece de la base con sus renglones, sus cláusulas y las " +
            "tareas anuladas que hubiera generado. No se puede recuperar.",
        )
      )
        return;
      err.textContent = "";
      try {
        await api.del(`/api/presupuestos/${id}?definitivo=1`);
        location.hash = "#/presupuestos";
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo borrar";
      }
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudo cargar el presupuesto: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
